import { useState } from "react";
import { Pencil } from "lucide-react";
import type { Varianta, ZakazkaDetail } from "@/api/schemas";
import { usePrava } from "@/api/hooks/useAuth";
import { formatCisloBezNul, formatEur } from "@/lib/format";
import { VETA } from "@/lib/prava";
import { jeTovarovaVarianta } from "@/lib/variantyZakazky";
import {
  PodmienkyPonukyDialog,
  type OdborPonuky,
} from "@/components/zakazka/PodmienkyPonukyDialog";

/**
 * Varianty v cene zákazky po odboroch — nábytok (aktívny výpočet, zákazka
 * bez výpočtu všetky časti výroby) a aktívny tovar. To isté pravidlo ako
 * `casti_zakazky.varianty_ceny` na serveri.
 */
export function odboryPonuky(zakazka: ZakazkaDetail): OdborPonuky[] {
  const varianty = (zakazka.varianty ?? []) as Varianta[];
  const nabytok = varianty.filter((v) => !jeTovarovaVarianta(v));
  const vNabytku = zakazka.cena_z_vyroby
    ? nabytok.filter((v) => v.je_vyrobna)
    : nabytok.filter((v) => v.id === zakazka.aktivna_varianta_id);
  const tovar = varianty.find((v) => v.id === zakazka.aktivna_tovar_varianta_id);
  const odbory: OdborPonuky[] = [];
  if (vNabytku.length > 0) odbory.push({ kluc: "nabytok", nazov: "Nábytok", varianty: vNabytku });
  if (tovar) odbory.push({ kluc: "tovar", nazov: "Tovar", varianty: [tovar] });
  return odbory;
}

function podmienky(v: Varianta | undefined): string {
  const koef = formatCisloBezNul(v?.koef_ponuky ?? "1");
  const zlava = Number(v?.sleva_pct ?? 0);
  return `×${koef}${zlava ? ` · zľava ${formatCisloBezNul(v?.sleva_pct)} %` : ""}`;
}

/**
 * Pás obchodných podmienok nad záložkami (KOLO 231) — user si vybral pás:
 * „Rozpočet X € → Ponuka Y €" s koeficientom a zľavou nábytku aj tovaru.
 * Ceruzka (len s právom podmienok — obchodník, admin) otvorí okno;
 * rozpočtár pás vidí len na čítanie.
 */
export function PasPonuky({ zakazka }: { zakazka: ZakazkaDetail }): JSX.Element | null {
  const prava = usePrava();
  const [otvorene, setOtvorene] = useState(false);
  const odbory = odboryPonuky(zakazka);
  if (odbory.length === 0) return null;

  const rozpocet = zakazka.cena_rozpoctu_s_dph;
  const ponuka = zakazka.cena_celkom_s_dph;
  const lisiSa = rozpocet != null && ponuka != null && Number(rozpocet) !== Number(ponuka);

  return (
    <div
      className="flex h-8 shrink-0 items-center gap-x-3 overflow-x-auto whitespace-nowrap rounded-md border bg-background-alt/40 px-3 text-sm"
      data-testid="pas-ponuky"
      title={prava.podmienky ? undefined : VETA.podmienky}
    >
      <span className="text-muted-foreground">
        Rozpočet <span className="font-mono text-foreground">{formatEur(rozpocet)}</span>
      </span>
      <span aria-hidden className="text-muted-foreground">
        →
      </span>
      <span className="text-muted-foreground">
        Ponuka{" "}
        <b
          className={lisiSa ? "font-mono text-primary" : "font-mono text-foreground"}
          data-testid="pas-ponuky-cena"
        >
          {formatEur(ponuka)}
        </b>
      </span>
      <span className="h-4 w-px shrink-0 bg-border" aria-hidden />
      {odbory.map((o) => (
        <span key={o.kluc} className="text-xs text-muted-foreground">
          {o.nazov.toLowerCase()} <span className="font-mono">{podmienky(o.varianty[0])}</span>
        </span>
      ))}
      {prava.podmienky ? (
        <button
          type="button"
          onClick={() => setOtvorene(true)}
          className="ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Upraviť podmienky ponuky"
          title="Koeficient a zľava ponuky pre nábytok a tovar"
        >
          <Pencil className="h-3.5 w-3.5" />
          Podmienky
        </button>
      ) : null}
      {otvorene ? (
        <PodmienkyPonukyDialog
          zakazkaId={zakazka.id}
          odbory={odbory}
          open
          onOpenChange={setOtvorene}
        />
      ) : null}
    </div>
  );
}
